import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks/useProfile';
import ProfileSkeleton from '../../components/community/profile/ProfileSkeleton';
import RecentTripsGrid from '../../components/community/profile/RecentTripsGrid';
import FollowButton from '../../components/community/profile/FollowButton';
import EditProfileDialog from '../../components/community/profile/EditProfileDialog';
import BadgeStrip from '../../components/community/badges/BadgeStrip';
import { Map, Navigation, Compass, CheckCircle, Edit3, Calendar, Globe, Check, BookOpen, PiggyBank } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';

const PublicProfile = () => {
  const { handle } = useParams();
  const { user } = useAuth();
  const { data, isLoading, error, setData } = useProfile(handle);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (!data || data.viewerState?.isSelf) return;
    trackEvent('public_profile_viewed', {
      handle: data.profile?.handle,
      is_verified: !!data.profile?.isVerified,
    });
  }, [data]);

  if (isLoading) return <ProfileSkeleton />;

  if (error || !data) {
    const safeName = user?.name ? user.name.toLowerCase().replace(/[^a-z0-9_]/g, '') : '';
    const isMyFallback =
      handle === safeName ||
      handle === user?.handle ||
      handle?.toLowerCase().replace(/[^a-z0-9_]/g, '') === safeName;

    if (isMyFallback && user) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 max-w-lg w-full text-center">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass size={40} className="text-teal-600" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 mb-2">Welcome to Community!</h1>
            <p className="text-gray-500 mb-8">You haven't set up your public profile handle yet. Set one up to start sharing itineraries!</p>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-bold transition-colors inline-block"
            >
              Set Up Profile
            </button>
            {isEditModalOpen && (
              <EditProfileDialog
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onProfileUpdated={() => window.location.reload()}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Compass size={40} className="text-gray-300" />
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-2">Profile Not Found</h1>
          <p className="text-gray-500 mb-8">This profile doesn't exist or has been removed.</p>
          <Link to="/community" className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl font-bold transition-colors inline-block">
            Back to Community
          </Link>
        </div>
      </div>
    );
  }

  const { profile, stats, recentItineraries, viewerState, badges } = data;
  const isSelf = viewerState?.isSelf;

  // This page is for viewing *other* people. A user's own profile — badges,
  // credits, stats and the edit affordances — lives on /account, so there is one
  // place to manage yourself instead of two that partially overlap. `replace`
  // keeps Back going wherever they came from rather than bouncing them here.
  if (isSelf) return <Navigate to="/account" replace />;

  const handleFollowChange = (delta) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stats: {
          ...prev.stats,
          followerCount: (prev.stats.followerCount || 0) + delta,
        },
        viewerState: {
          ...prev.viewerState,
          isFollowing: delta > 0,
        }
      };
    });
  };

  const joinedDate = new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Cover. A slim teal band rather than a 288px rainbow gradient with blurred
          blobs — the profile is the subject, not the backdrop. */}
      <div className="h-40 w-full bg-gradient-to-r from-teal-700 to-teal-600" />

      <div className="container mx-auto px-4 sm:px-6 max-w-5xl -mt-20 relative z-10">
        {/* Identity card */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="relative shrink-0 -mt-16 sm:-mt-14">
              <div className="w-24 h-24 rounded-2xl bg-teal-700 ring-4 ring-white shadow-sm flex items-center justify-center text-white text-3xl font-semibold">
                {profile.name ? profile.name.charAt(0).toUpperCase() : '?'}
              </div>
              {profile.isVerified && (
                <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-teal-600 rounded-full ring-4 ring-white flex items-center justify-center">
                  <CheckCircle size={14} className="text-white" />
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold tracking-[-0.02em] text-gray-900 truncate">
                    {profile.name}
                  </h1>
                  <p className="text-sm text-gray-500 mt-0.5">@{profile.handle}</p>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                    {badges && badges.length > 0 && (
                      <BadgeStrip badges={badges} isVerified={profile.isVerified} />
                    )}
                    <span className="flex items-center gap-1.5 text-xs text-gray-400">
                      <Calendar size={13} /> Joined {joinedDate}
                    </span>
                  </div>
                </div>

                <div className="shrink-0">
                  {isSelf ? (
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <Edit3 size={14} /> Edit profile
                    </button>
                  ) : viewerState ? (
                    <FollowButton
                      handle={profile.handle}
                      initialIsFollowing={viewerState.isFollowing}
                      onFollowChange={handleFollowChange}
                      className="px-5 py-2 text-sm"
                    />
                  ) : (
                    <FollowButton handle={profile.handle} className="px-5 py-2 text-sm" />
                  )}
                </div>
              </div>

              {profile.bio && (
                <p className="text-[15px] text-gray-600 leading-relaxed mt-4 max-w-2xl break-words">
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          {/* Stats. One divided row instead of five floating columns — the same
              treatment the trip pages use, so the app reads as one system. */}
          <div className="mt-7 pt-6 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-5 divide-x divide-y sm:divide-y-0 divide-gray-100">
            {[
              { label: 'Followers', value: stats?.followerCount || 0 },
              { label: 'Following', value: stats?.followingCount || 0 },
              { label: 'Countries', value: stats?.countriesVisited || 0, Icon: Globe },
              { label: 'Completed', value: stats?.tripsCompleted || 0, Icon: CheckCircle },
              { label: 'Published', value: stats?.publishedTrips || 0, Icon: BookOpen },
            ].map((stat) => (
              <div key={stat.label} className="px-4 py-2 first:pl-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xl font-semibold text-gray-900 tabular-nums">{stat.value}</span>
                  {stat.Icon && <stat.Icon size={14} className="text-gray-300" />}
                </div>
                <span className="block text-[10px] uppercase tracking-[0.14em] text-gray-400 font-semibold mt-1">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Credits — its own card, owner only. */}
        {isSelf && stats?.credits !== undefined && (
          <div className="mt-5 bg-white rounded-2xl border border-gray-200/70 shadow-sm p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <span className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                <PiggyBank size={20} />
              </span>
              <div className="min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.14em] text-gray-400 font-semibold">
                  Available RoamPoints
                </span>
                <span className="block text-xl font-semibold text-gray-900 tabular-nums">
                  {stats.credits}
                </span>
              </div>
            </div>
            <Link
              to="/account?tab=wallet"
              onClick={() => trackEvent('credits_redeem_clicked', {})}
              className="shrink-0 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold text-sm hover:bg-teal-800 transition-colors"
            >
              Redeem
            </Link>
          </div>
        )}

        {/* Recent Itineraries */}
        <div className="mt-12">
          <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-gray-900">
            Recent adventures
          </h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">
            Curated itineraries by {profile.name.split(' ')[0]}
          </p>
          <RecentTripsGrid trips={recentItineraries} />
        </div>
      </div>

      {isEditModalOpen && (
        <EditProfileDialog
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onProfileUpdated={() => {
            // Let the edit dialog mutate local state or trigger a reload.
            // We can just reload the window for a fresh state for MVP to ensure everything is perfect.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default PublicProfile;
